import { Button, Input } from "@/shared";
import {
    Document,
    Font,
    Page,
    pdf,
    Text,
    View,
    StyleSheet
} from "@react-pdf/renderer";
import axios from "axios";
import { saveAs } from "file-saver";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Cookies from 'js-cookie';
import { API_URL } from "@/shared/utils/API_URL";
import MainLayout from "@/shared/components/MainLayout";
import Header from "@/shared/components/ui/Header";

Font.register({
    family: "Tajawal",
    src: "https://fonts.gstatic.com/s/tajawal/v10/Iura6YBj_oCad4k1rzaLCr5IlLA.ttf",
});

const STORAGE_KEY = "print_settings_config";

const getStyles = (customStyles: any) => StyleSheet.create({
    page: {
        padding: 10,
        fontFamily: "Tajawal",
        color: 'black'
    },
    container: {
        flexDirection: 'column',
        borderRadius: 4,
        padding: 8,
        height: '100%',
        justifyContent: 'space-between',
    },
    receiptTitle: {
        fontSize: customStyles.headerFontSize + 2,
        textAlign: 'center',
        fontWeight: 'bold',
        marginBottom: 2,
    },
    dateText: {
        fontSize: customStyles.labelFontSize,
        textAlign: 'right',
        marginBottom: 4,
    },
    header: {
        fontSize: customStyles.headerFontSize,
        textAlign: 'center',
        marginBottom: 5,
        borderBottom: '0.5pt solid #eee',
        paddingBottom: 2,
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginVertical: 2,
    },
    section: {
        flex: 1,
        alignItems: 'center',
    },
    label: {
        fontSize: customStyles.labelFontSize,
        marginBottom: 2,
    },
    value: {
        fontSize: customStyles.valueFontSize,
        fontWeight: 'bold',
    },
    footer: {
        fontSize: 6,
        textAlign: 'center',
        marginTop: 4,
    }
});

const PrintAccount = () => {
    const { t } = useTranslation();
    const { id, name, balance } = useParams();
    const [loading, setLoading] = useState(false);

    const getInitialState = () => {
        const saved = localStorage.getItem(STORAGE_KEY);
        const defaultState = {
            width: 60,
            height: 40,
            factor: 2.83,
            headerFontSize: 14,
            labelFontSize: 8,
            valueFontSize: 12,
            payment: 0,
            discount: 0,
            displaySetting: false,
            showReceiptTitle: false,
            showDate: false,
        };

        if (saved) {
            const parsed = JSON.parse(saved);
            return { ...parsed, payment: 0, discount: 0 };
        }
        return defaultState;
    };

    const [dimensions, setDimensions] = useState(getInitialState);

    useEffect(() => {
        const { payment, discount, ...settingsToSave } = dimensions;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsToSave));
    }, [dimensions]);

    const oldBalance = Number(balance) || 0;
    const payment = Number(dimensions.payment) || 0;
    const discount = Number(dimensions.discount) || 0;
    const finalBalance = oldBalance - (payment + discount);

    const handleChange = (e: any) => {
        const { id, value } = e.target;
        setDimensions((prev: any) => ({ ...prev, [id]: value }))
    }

    const exportPdf = async () => {

        setLoading(true);

        const factor = Number(dimensions.factor) || 1;
        const pageWidth = Number(dimensions.width) * factor;
        const pageHeight = Number(dimensions.height) * factor;
        const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');

        const dynamicStyles = getStyles({
            headerFontSize: Number(dimensions.headerFontSize),
            labelFontSize: Number(dimensions.labelFontSize),
            valueFontSize: Number(dimensions.valueFontSize),
        });

        const MyDocument = (
            <Document>
                <Page size={{ width: `${pageWidth}mm`, height: `${pageHeight}mm` }} style={dynamicStyles.page}>
                    <View style={dynamicStyles.container}>
                        {dimensions.showReceiptTitle && <Text style={dynamicStyles.receiptTitle}>{t("سند قبض")}</Text>}
                        {dimensions.showDate && <Text style={dynamicStyles.dateText}>{today}</Text>}

                        <Text style={dynamicStyles.header}>{decodeURIComponent(name || '')}</Text>

                        <View style={dynamicStyles.row}>
                            <View style={dynamicStyles.section}>
                                <Text style={dynamicStyles.label}>{t("old balance")}</Text>
                                <Text style={dynamicStyles.value}>{oldBalance.toLocaleString()}</Text>
                            </View>
                        </View>

                        <View style={{ borderBottom: '0.5pt solid #eee' }} />

                        <View style={dynamicStyles.row}>
                            <View style={dynamicStyles.section}>
                                <Text style={dynamicStyles.label}>{t("discount")}</Text>
                                <Text style={dynamicStyles.value}>{discount.toLocaleString()}</Text>
                            </View>
                            <View style={{ width: 1, height: '80%', backgroundColor: '#eee' }} />
                            <View style={dynamicStyles.section}>
                                <Text style={dynamicStyles.label}>{t("payment")}</Text>
                                <Text style={dynamicStyles.value}>{payment.toLocaleString()}</Text>
                            </View>
                        </View>

                        <View style={[dynamicStyles.section, { marginTop: 4, padding: 4 }]}>
                            <Text style={dynamicStyles.label}>{t("Total balance")}</Text>
                            <Text style={[dynamicStyles.value, { fontSize: Number(dimensions.valueFontSize) + 2, color: '#1a365d' }]}>
                                {finalBalance.toLocaleString()}
                            </Text>
                        </View>
                    </View>
                </Page>
            </Document>
        );
        const licence_id = localStorage.getItem('licence_id');
        const sessionId = Cookies.get('sessionId');
        try {

            const response = await axios.post(API_URL, {
                q: `insert into npostmoney values ( null, 'MoneyIn', 'MoneyIn', '', NOW(), '${new Date().toLocaleString('sv-SE')}', 0, 1, ${id}, ${dimensions.payment}, 0, 'Y')`
            },
                {
                    params: {
                        t: 'up',
                        phone: '',
                        id: licence_id,
                        s: sessionId
                    },
                })
            console.log(response);

            const blob = await pdf(MyDocument).toBlob();
            saveAs(blob, `${decodeURIComponent(name || '') || 'account_details'}.pdf`);
        } catch (error) {
            console.error("PDF Generation Error:", error);
        }
        finally {
            setLoading(false)
        }
    }

    return (
       <MainLayout>
        <Header title={t("receipt")}/>
         <div className="bg-gray-50 p-6 flex items-center justify-center">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-8">
                <div className="flex items-center gap-3 mb-6 border-b pb-4">
                    <h2 className="text-xl font-bold text-gray-800">{t("receipt")}</h2>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <div className="col-span-full space-y-4">
                        <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                            <div>
                                <label className="block text-xs font-bold text-blue-700 mb-1">{t("Payment")}</label>
                                <Input id="payment" type="number" value={dimensions.payment} onChange={handleChange} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-blue-700 mb-1">{t("Discount")}</label>
                                <Input id="discount" type="number" value={dimensions.discount} onChange={handleChange} />
                            </div>
                        </div>

                        <div className="col-span-full grid grid-cols-2 gap-4 py-4 border-t border-b border-gray-100">
                            <div className="flex flex-col items-center">
                                <span className="text-[11px] text-gray-400 uppercase font-medium">{t("old balance")}</span>
                                <span className="text-lg font-semibold text-gray-700">{oldBalance.toLocaleString()}</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <span className="text-[11px] text-blue-500 uppercase font-bold">{t("Total balance")}</span>
                                <span className="text-lg font-bold text-blue-900">{finalBalance.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="col-span-full flex flex-wrap gap-6 items-center bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                        <input
                            id="displaySetting"
                            type="checkbox"
                            checked={dimensions.displaySetting}
                            onChange={(e) => setDimensions((prev: any) => ({ ...prev, displaySetting: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 border"
                        />
                        <label htmlFor="displaySetting" className="text-sm font-medium text-gray-700">{t("displaySetting")}</label>
                    </div>
                    {dimensions?.displaySetting && (
                        <>
                            <div className="flex items-center gap-2">
                                <input
                                    id="showReceiptTitle"
                                    type="checkbox"
                                    checked={dimensions.showReceiptTitle}
                                    onChange={(e) => setDimensions((prev: any) => ({ ...prev, showReceiptTitle: e.target.checked }))}
                                    className="w-4 h-4 text-blue-600 border"
                                />
                                <label htmlFor="showReceiptTitle" className="text-sm font-medium text-gray-700">{t("Show title")}</label>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    id="showDate"
                                    type="checkbox"
                                    checked={dimensions.showDate}
                                    onChange={(e) => setDimensions((prev: any) => ({ ...prev, showDate: e.target.checked }))}
                                    className="w-4 h-4 text-blue-600 border"
                                />
                                <label htmlFor="showDate" className="text-sm font-medium text-gray-700">{t("Show Date")}</label>
                            </div>

                        </>
                    )}

                </div>
                {dimensions.displaySetting && (
                    <>
                        <div className="space-y-4 col-span-full border-b pb-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t("Width (mm)")}</label>
                                    <Input id="width" type="number" value={dimensions.width} onChange={handleChange} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t("Height (mm)")}</label>
                                    <Input id="height" type="number" value={dimensions.height} onChange={handleChange} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t("Factor")}</label>
                                    <Input id="factor" type="number" value={dimensions.factor} onChange={handleChange} />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 col-span-full border-b pb-4 bg-gray-50 p-4 rounded-lg">
                            <h3 className="text-sm font-bold text-green-600 uppercase tracking-widest">{t("Font Sizes")}</h3>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t("Customer Name")}</label>
                                    <Input id="headerFontSize" type="number" value={dimensions.headerFontSize} onChange={handleChange} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t("Labels")}</label>
                                    <Input id="labelFontSize" type="number" value={dimensions.labelFontSize} onChange={handleChange} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t("Numbers")}</label>
                                    <Input id="valueFontSize" type="number" value={dimensions.valueFontSize} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <Button
                    onClick={exportPdf}
                    disabled={loading}
                    loading={loading}
                    className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl shadow-lg transition-all active:scale-[0.98]"
                >
                    {t("Save & Print PDF")}
                </Button>
            </div>
        </div>
       </MainLayout>
    );
}

export default PrintAccount;